// What is left to set up in a project (Get started, Console D): six steps, each read from the
// product's own data, in the order a team takes them. The page (routes/Setup.tsx) and the
// sidebar's ring share this hook, so they always agree. Read once and kept for a while: the page
// itself polls while it waits for the first session.
import { useQuery } from '@tanstack/react-query'
import {
  agentGrantsQuery,
  appsQuery,
  fetchSessions,
  invitationsQuery,
  membersQuery,
  meQuery,
  missingSymbolsQuery,
  projectsQuery,
  projectTokensQuery,
  type SessionsPage,
  windowReleasesQuery,
} from '../api/queries'
import type { AgentGrant, App, Invitation, Member, OrgMembership, Project } from '../api/types'
import type { MessageId } from '../i18n'
import { sandboxFor } from '../sandbox'
import { releasesNewestFirst } from './releases'

export const SETUP_ORDER = ['project', 'sdk', 'team', 'agent', 'symbols', 'plan'] as const
export type SetupStep = (typeof SETUP_ORDER)[number]
export const SETUP_STEPS = SETUP_ORDER.length

/** Each step's title. */
export const STEP_TITLE: Record<SetupStep, MessageId> = {
  project: 'setup.step.project',
  sdk: 'setup.step.sdk',
  team: 'setup.step.team',
  agent: 'setup.step.agent',
  symbols: 'setup.step.symbols',
  plan: 'setup.step.plan',
}

/** A title inside a sentence ("Next: install the SDK"): its first letter in lower case. */
/** The steps a coding agent can do for you: installing the SDK, and the symbol upload in CI. */
const AGENT_STEPS = ['sdk', 'symbols'] as const satisfies readonly SetupStep[]
export type AgentStep = (typeof AGENT_STEPS)[number]

/**
 * The step "Or ask your agent" offers a prompt for (APS-388): the next step when an agent can do
 * it, else the first such step not done yet; none when an agent can do nothing left, or while a
 * step is still unknown.
 */
export function askStep(
  steps: Record<SetupStep, boolean | undefined>,
  next: SetupStep | undefined,
): AgentStep | undefined {
  if (next && (AGENT_STEPS as readonly SetupStep[]).includes(next)) return next as AgentStep
  return AGENT_STEPS.find((s) => steps[s] === false)
}

export const lowerFirst = (s: string): string => s.charAt(0).toLocaleLowerCase() + s.slice(1)

const STALE_MS = 10 * 60_000
const HOUR = 3_600_000

/** The query key of "any session of the project". */
const anySessionKey = (projectId: string) => ['project', projectId, 'setup', 'any-session']

/** Whether each step is done; undefined while its data is loading or could not be read. */
export function stepsDone(d: {
  project: Project | undefined
  apps: App[] | undefined
  hasSession: boolean | undefined
  members: Member[] | undefined
  grants: AgentGrant[] | undefined
  tokens: unknown[] | undefined
  newestRelease: string | null | undefined
  missing: number | undefined
  org: OrgMembership | undefined
}): Record<SetupStep, boolean | undefined> {
  const symbols =
    d.newestRelease === null
      ? false
      : d.newestRelease === undefined || d.missing === undefined
        ? undefined
        : d.missing === 0
  return {
    project: d.project ? true : undefined,
    sdk:
      d.apps === undefined
        ? undefined
        : d.apps.length === 0
          ? false
          : d.hasSession === undefined
            ? undefined
            : d.hasSession,
    team: d.members ? d.members.length > 1 : undefined,
    agent:
      d.grants === undefined || d.tokens === undefined
        ? undefined
        : d.grants.length + d.tokens.length > 0,
    symbols,
    plan: d.org ? d.org.plan !== 'trial' : undefined,
  }
}

export interface SetupProgress {
  /** Each step: done, not done, or unknown. */
  steps: Record<SetupStep, boolean | undefined>
  /** Steps whose data could not be read: shown as not done, with the error. */
  failed: ReadonlySet<SetupStep>
  /** The project's apps could not be read (no access, or no such project): the page refuses. */
  error: unknown
  /** Steps done; undefined until every step is known. */
  done: number | undefined
  /** The first step not done, when every step is known. */
  next: SetupStep | undefined
  /** The data the steps were read from, for the page. */
  data: {
    org: OrgMembership | undefined
    project: Project | undefined
    apps: App[] | undefined
    latestSession: SessionsPage['sessions'][number] | undefined
    members: Member[] | undefined
    /** Pending invitations; owners and admins only, undefined for members. */
    invitations: Invitation[] | undefined
    grants: AgentGrant[] | undefined
    tokens: { name: string; created_at: string }[] | undefined
    newestRelease: string | null | undefined
    missing: { app_id: string; image_name: string; arch: string; issues: string[] }[] | undefined
  }
  refetchSymbols: () => Promise<unknown>
}

export function useSetupProgress(orgId: string, projectId: string): SetupProgress {
  const sandbox = !!sandboxFor(projectId)
  const on = !sandbox
  const me = useQuery(meQuery)
  const org = me.data?.orgs.find((o) => o.id === orgId)
  const admin = org?.role === 'owner' || org?.role === 'admin'
  const projects = useQuery({ ...projectsQuery(orgId), enabled: on })
  const apps = useQuery({ ...appsQuery(orgId, projectId), enabled: on })
  const hasApp = (apps.data?.length ?? 0) > 0
  // Any session of the project in the read API's longest window.
  const sessions = useQuery({
    queryKey: anySessionKey(projectId),
    // Every build: a project's first data is usually a debug build's (APS-508).
    queryFn: () => fetchSessions(projectId, { days: 30, limit: 1, build: 'all' }),
    enabled: on && hasApp,
    staleTime: STALE_MS,
  })
  const members = useQuery({ ...membersQuery(orgId), enabled: on, staleTime: STALE_MS })
  const invitations = useQuery({
    ...invitationsQuery(orgId),
    enabled: on && admin,
    staleTime: STALE_MS,
  })
  const grants = useQuery({ ...agentGrantsQuery(orgId), enabled: on, staleTime: STALE_MS })
  const tokens = useQuery({
    ...projectTokensQuery(orgId, projectId),
    enabled: on,
    staleTime: STALE_MS,
  })
  // The newest release of the last 30 days and its images without symbols, as the sidebar's
  // Symbols count reads them (same keys, one request).
  const now = Math.floor(Date.now() / HOUR) * HOUR
  const releases = useQuery({
    ...windowReleasesQuery(projectId, 30, now),
    enabled: on && hasApp,
  })
  const newestRelease = !hasApp
    ? apps.data
      ? null
      : undefined
    : releases.data
      ? (releasesNewestFirst(releases.data)[0]?.release ?? null)
      : undefined
  const missing = useQuery({
    ...missingSymbolsQuery(projectId, newestRelease ?? ''),
    enabled: on && !!newestRelease,
  })

  const project = projects.data?.find((p) => p.id === projectId)
  const latest = sessions.data?.sessions[0]
  const steps = stepsDone({
    project,
    apps: apps.data,
    hasSession: sessions.data ? sessions.data.sessions.length > 0 : undefined,
    members: members.data,
    grants: grants.data,
    tokens: tokens.data,
    newestRelease,
    missing: missing.data?.missing.length,
    org,
  })
  // A step whose data failed counts as not done, so the rest still add up.
  const failed = new Set<SetupStep>()
  const fail = (step: SetupStep, ...queries: { isError: boolean }[]) => {
    if (queries.some((q) => q.isError)) {
      failed.add(step)
      steps[step] = false
    }
  }
  fail('project', projects)
  fail('sdk', apps, sessions)
  fail('team', members)
  fail('agent', grants, tokens)
  fail('symbols', releases, missing)
  fail('plan', me)
  const known = !sandbox && SETUP_ORDER.every((s) => steps[s] !== undefined)
  return {
    steps,
    failed,
    error: apps.error ?? undefined,
    done: known ? SETUP_ORDER.filter((s) => steps[s]).length : undefined,
    next: known ? SETUP_ORDER.find((s) => !steps[s]) : undefined,
    data: {
      org,
      project,
      apps: apps.data,
      latestSession: latest,
      members: members.data,
      invitations: invitations.data,
      grants: grants.data,
      tokens: tokens.data,
      newestRelease,
      missing: missing.data?.missing,
    },
    refetchSymbols: () => missing.refetch(),
  }
}
